// Appels d'API des PIÈCES TERRAIN (migration 058) : photos, vidéos, sons et
// notes pris sur place, rangés dans l'étude.
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, putFile, type UploadTarget as Upload } from '../api/client';

export type FieldKind = 'photo' | 'video' | 'audio' | 'note';

export interface FieldItem {
  id: string;
  project_id: string;
  study_id: string;
  interview_id: string | null;
  interview_participant: string | null;
  kind: FieldKind;
  caption: string | null;
  note_text: string | null;
  taken_at: string;
  file_name: string | null;
  file_type: string | null;
  file_size: number | string | null;
  file_status: 'none' | 'uploading' | 'ready';
  created_by_name: string | null;
  created_at: string;
}

export const fieldFileUrl = (id: string) => `/api/field-items/${encodeURIComponent(id)}/file`;

const key = (studyId: string) => ['field-items', studyId] as const;

export function useFieldItems(studyId: string | null) {
  return useQuery({
    queryKey: key(studyId ?? ''),
    queryFn: () => api.get<{ items: FieldItem[]; uploads: boolean }>(`/api/studies/${encodeURIComponent(studyId!)}/field-items`),
    enabled: Boolean(studyId),
  });
}

export interface NewFieldItem {
  kind: FieldKind;
  caption?: string;
  noteText?: string;
  interviewId?: string | null;
  takenAt?: string;
  file?: File;
  /** L'accord des personnes visibles ou enregistrées (exigé pour un fichier). */
  consent?: boolean;
  onProgress?: (ratio: number) => void;
}

export function useAddFieldItem(studyId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ file, onProgress, ...body }: NewFieldItem) => {
      const created = await api.post<{ item: FieldItem; upload?: Upload }>(
        `/api/studies/${encodeURIComponent(studyId)}/field-items`,
        file ? { ...body, file: { name: file.name, type: file.type, size: file.size } } : body,
      );
      if (!file || !created.upload) return created.item;
      await putFile(created.upload, file, onProgress ?? (() => undefined));
      const done = await api.post<{ item: FieldItem }>(`/api/field-items/${encodeURIComponent(created.item.id)}/complete`);
      return done.item;
    },
    onSettled: () => qc.invalidateQueries({ queryKey: key(studyId) }),
  });
}

export function useUpdateFieldItem(studyId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, caption, noteText }: { id: string; caption?: string; noteText?: string }) =>
      api.request<{ item: FieldItem }>(`/api/field-items/${encodeURIComponent(id)}`, { method: 'PATCH', body: { caption, noteText } }),
    onSuccess: () => qc.invalidateQueries({ queryKey: key(studyId) }),
  });
}

export function useDeleteFieldItem(studyId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.del<{ deleted: boolean }>(`/api/field-items/${encodeURIComponent(id)}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: key(studyId) }),
  });
}
